import { Check, EyeOff, RefreshCw, RotateCcw, Users } from "lucide-react";
import React from "react";

import { Button } from "@/components/ui/button";
import { fmtDateTime } from "@/lib/format";
import { BottomSheet } from "@/lib/scan-ui/overlays";

/**
 * Меню шапки идущей инвентаризации: всё, что НЕ скан.
 *
 * Завершение, пересъёмка остатков и «кто считает» за смену нужны по одному
 * разу, а место занимали внизу рядом со сканом — главным действием экрана.
 * Здесь они крупными строками под палец (≥ 44 px) и с подписью «зачем»:
 * человек с ТСД в руках не должен гадать, что сделает «переснять».
 *
 * Компонент чистый: своих запросов нет, каждое действие СНАЧАЛА закрывает
 * шторку, чтобы диалог подтверждения открылся поверх пустого экрана, а не
 * поверх меню.
 */
export function SessionMenu({
  readOnly,
  snapshotAt,
  docNumber,
  participants,
  resnapping,
  onClose,
  onFinish,
  onReopen,
  onResnap,
  onWorkers,
  onExcluded,
}: {
  /** Смена завершена: вместо «Завершить» — «Вернуть в работу», пересъёмки нет. */
  readOnly: boolean;
  snapshotAt: string | null;
  /** Номер документа 1С, если он уже создан: завершение его ОБНОВИТ. */
  docNumber: string | null;
  participants: number;
  resnapping: boolean;
  onClose: () => void;
  onFinish: () => void;
  onReopen: () => void;
  onResnap: () => void;
  onWorkers: () => void;
  /** «Что не предлагать при скане» — правило СКЛАДА, живёт дольше смены. */
  onExcluded: () => void;
}): React.ReactElement {
  /** Действие всегда гасит шторку: подтверждение — уже поверх экрана смены. */
  const pick = (run: () => void) => () => {
    onClose();
    run();
  };
  const snapshot = fmtDateTime(snapshotAt);
  return (
    <BottomSheet onClose={onClose} panelClassName="p-3">
      <h3 className="mb-2 text-base font-semibold">Инвентаризация</h3>
      <div className="grid gap-2">
        {readOnly ? (
          <MenuItem
            icon={<RotateCcw />}
            label="Вернуть в работу"
            hint={
              docNumber
                ? `посчитанное останется, документ ${docNumber} обновится при повторном завершении`
                : "посчитанное останется, можно досчитать"
            }
            onClick={pick(onReopen)}
          />
        ) : (
          <MenuItem
            primary
            icon={<Check />}
            label="Завершить"
            hint={
              docNumber
                ? `документ ${docNumber} обновится — номер тот же`
                : "создать документ инвентаризации в 1С"
            }
            onClick={pick(onFinish)}
          />
        )}
        {!readOnly && (
          <MenuItem
            icon={<RefreshCw className={resnapping ? "animate-spin" : ""} />}
            label="Переснять учётные остатки"
            hint={
              snapshot
                ? `остатки на ${snapshot} — переснять, посчитанное сохранится`
                : "взять остатки из 1С заново, посчитанное сохранится"
            }
            disabled={resnapping}
            onClick={pick(onResnap)}
          />
        )}
        {!readOnly && (
          <MenuItem
            icon={<EyeOff />}
            label="Что не предлагать при скане"
            hint="характеристики, которых на этом складе не бывает (ОПТ на складе ФБС) — запомнится для склада"
            onClick={pick(onExcluded)}
          />
        )}
        <MenuItem
          icon={<Users />}
          label="Кто считает"
          hint={
            participants > 1
              ? `в смене ${participants} чел. — кто сколько насчитал`
              : "кто сколько насчитал за смену"
          }
          onClick={pick(onWorkers)}
        />
      </div>
    </BottomSheet>
  );
}

/** Строка меню: иконка, действие и подпись «зачем». Высота — под палец. */
function MenuItem({
  icon,
  label,
  hint,
  onClick,
  disabled,
  primary,
}: {
  icon: React.ReactNode;
  label: string;
  hint: string;
  onClick: () => void;
  disabled?: boolean;
  primary?: boolean;
}): React.ReactElement {
  return (
    <Button
      variant={primary ? "default" : "outline"}
      className="h-auto min-h-[56px] justify-start whitespace-normal py-2.5 text-left"
      onClick={onClick}
      disabled={disabled}
    >
      {icon}
      <span className="flex min-w-0 flex-col">
        <span className="text-sm font-semibold">{label}</span>
        <span className={"text-xs font-normal " + (primary ? "opacity-80" : "text-slate-500")}>
          {hint}
        </span>
      </span>
    </Button>
  );
}
